import { Dispatch } from 'redux';
import { Rep, Workout } from '../classes/Workout';
import { handleActivityStatus, handleTimerStatus, handleCurrentRepUpdate } from '../actions/activities.actions';

/**
 * Simple, Reliable Workout Manager
 * 
 * This class provides a single source of truth for workout state management.
 * It follows the principle of single responsibility and clear state transitions.
 */

export interface WorkoutState {
  phase: 'idle' | 'ready' | 'active' | 'paused' | 'completed';
  repPhase: 'wait_start' | 'hang' | 'rest' | 'wait_hang' | 'wait_rest' | 'wait_release';
  currentRepIndex: number;
  totalReps: number;
  isBluetoothReady: boolean;
}

export interface WorkoutConfig {
  workout: Workout;
  userWeight: number;
  addedWeights: number;
  bluetoothConnected: boolean;
  bluetoothCalibrated: boolean;
}

export class SimpleWorkoutManager {
  private static instance: SimpleWorkoutManager;
  private state: WorkoutState;
  private config: WorkoutConfig | null = null;
  private dispatch: Dispatch<any> | null = null;

  private constructor() {
    this.state = this.getInitialState();
  }

  public static getInstance(): SimpleWorkoutManager {
    if (!SimpleWorkoutManager.instance) {
      SimpleWorkoutManager.instance = new SimpleWorkoutManager();
    }
    return SimpleWorkoutManager.instance;
  }

  private getInitialState(): WorkoutState {
    return {
      phase: 'idle',
      repPhase: 'wait_start',
      currentRepIndex: 0,
      totalReps: 0,
      isBluetoothReady: false,
    };
  }

  /**
   * Initialize the workout manager with configuration
   */
  public initialize(config: WorkoutConfig, dispatch: Dispatch<any>): void {
    if (this.isInitialized()) {
      console.warn('SimpleWorkoutManager: Already initialized, skipping duplicate init');
      return;
    }

    if (!config.workout || !config.workout.content || !config.workout.content.reps) {
      console.warn('SimpleWorkoutManager: Cannot initialize - workout data not ready', {
        workout: !!config.workout,
        content: !!config.workout?.content,
        reps: !!config.workout?.content?.reps
      });
      return;
    }

    this.config = config;
    this.dispatch = dispatch;
    this.state = {
      ...this.getInitialState(),
      totalReps: config.workout.content.reps.length,
      isBluetoothReady: config.bluetoothConnected && config.bluetoothCalibrated,
    };
    
    console.log('🔧 SimpleWorkoutManager: Initialization state:', {
      bluetoothConnected: config.bluetoothConnected,
      bluetoothCalibrated: config.bluetoothCalibrated,
      isBluetoothReady: this.state.isBluetoothReady,
      totalReps: this.state.totalReps
    });
    
    // Set initial activity status based on Bluetooth readiness
    if (this.state.isBluetoothReady) {
      // If Bluetooth is ready, set to wait_start to indicate ready to begin
      console.log('✅ SimpleWorkoutManager: Bluetooth ready - setting activityStatus to wait start');
      this.dispatch(handleActivityStatus('wait start'));
      this.dispatch(handleTimerStatus('stop'));
      this.dispatch(handleCurrentRepUpdate(this.getCurrentRep()));
    } else {
      // If Bluetooth not ready, set to calibrate to indicate setup needed
      console.log('⏳ SimpleWorkoutManager: Bluetooth not ready - setting activityStatus to calibrate');
      this.dispatch(handleActivityStatus('calibrate'));
      this.dispatch(handleTimerStatus('stop'));
    }
  }

  /**
   * Update configuration (e.g., when Bluetooth status changes)
   */
  public updateConfig(updates: Partial<WorkoutConfig>): void {
    if (!this.config) return;
    
    const wasBluetoothReady = this.state.isBluetoothReady;
    this.config = { ...this.config, ...updates };
    this.state.isBluetoothReady = this.config.bluetoothConnected && this.config.bluetoothCalibrated;
    
    // Handle state transition when Bluetooth becomes ready
    if (!wasBluetoothReady && this.state.isBluetoothReady) {
      console.log('SimpleWorkoutManager: Bluetooth became ready - transitioning to wait start');
      this.dispatch(handleActivityStatus('wait start'));
      this.dispatch(handleCurrentRepUpdate(this.getCurrentRep()));
      
      // Auto-start workout if it's initialized and ready
      if (this.isInitialized()) {
        console.log('🚀 Auto-starting workout - Bluetooth became ready');
        setTimeout(() => {
          this.startWorkout();
        }, 500);
      }
    } else if (wasBluetoothReady && !this.state.isBluetoothReady) {
      console.log('SimpleWorkoutManager: Bluetooth became not ready - transitioning to calibrate');
      this.dispatch(handleActivityStatus('calibrate'));
    }
    
    console.log('SimpleWorkoutManager: Config updated', {
      bluetoothConnected: this.config.bluetoothConnected,
      bluetoothCalibrated: this.config.bluetoothCalibrated,
      isBluetoothReady: this.state.isBluetoothReady,
    });
  }

  /**
   * Start the workout (transition from wait_start to get ready phase)
   */
  public startWorkout(): void {
    if (!this.isInitialized() || !this.dispatch) {
      console.error('SimpleWorkoutManager: Not initialized');
      return;
    }

    if (!this.state.isBluetoothReady) {
      console.warn('SimpleWorkoutManager: Cannot start workout - Bluetooth not ready');
      return;
    }

    // Only start if we're in the right state
    if (this.state.phase !== 'idle' && this.state.phase !== 'ready') {
      console.warn('SimpleWorkoutManager: Cannot start workout - not in idle or ready state');
      return;
    }

    this.state.phase = 'ready';
    this.state.repPhase = 'wait_start';
    this.state.currentRepIndex = 0;

    // Update Redux state - set to get ready phase
    this.dispatch(handleActivityStatus('get ready'));
    this.dispatch(handleCurrentRepUpdate(this.getCurrentRep()));
    this.dispatch(handleTimerStatus('reset'));

    console.log('SimpleWorkoutManager: Workout started - entering get ready phase');
  }

  /**
   * Start the get ready countdown (3 seconds)
   */
  public startGetReadyCountdown(): void {
    if (!this.isInitialized() || !this.dispatch) {
      console.error('SimpleWorkoutManager: Not initialized');
      return;
    }

    if (this.state.phase !== 'ready' || this.state.repPhase !== 'wait_start') {
      console.warn('SimpleWorkoutManager: Cannot start countdown - not in ready state');
      return;
    }

    console.log('SimpleWorkoutManager: Starting 3-second get ready countdown');
    
    // Start the countdown timer
    console.log('SimpleWorkoutManager: Dispatching timer start for get ready countdown');
    this.dispatch(handleTimerStatus('start'));
    
    // After 3 seconds, wait for user to pull before starting the first rep
    setTimeout(() => {
      console.log('SimpleWorkoutManager: Get ready countdown finished, waiting for first pull');
      this.transitionToWaitHang();
    }, 3000); // 3 second countdown
  }

  /**
   * Start the current rep
   */
  public startRep(): void {
    if (!this.isInitialized() || !this.dispatch) {
      console.error('SimpleWorkoutManager: Not initialized');
      return;
    }

    const currentRep = this.getCurrentRep();
    if (!currentRep) {
      console.error('SimpleWorkoutManager: No current rep to start');
      return;
    }

    this.state.phase = 'active';
    this.state.repPhase = currentRep.intensity > 0 ? 'hang' : 'rest';

    // Update Redux state
    this.dispatch(handleActivityStatus(this.state.repPhase));
    
    console.log('SimpleWorkoutManager: Dispatching timer reset');
    this.dispatch(handleTimerStatus('reset')); // Reset timer first
    
    // Add a small delay to ensure reset is processed before start
    setTimeout(() => {
      console.log('SimpleWorkoutManager: Dispatching timer start');
      this.dispatch(handleTimerStatus('start')); // Then start it
    }, 50);

    console.log('SimpleWorkoutManager: Rep started', {
      repIndex: this.state.currentRepIndex,
      phase: this.state.repPhase,
      time: currentRep.time,
      intensity: currentRep.intensity,
    });
  }

  /**
   * Transition to waiting-for-pull state (after rest timer or get-ready countdown).
   * Called from ExerciseManager when the rest timer fires 'done'.
   */
  public transitionToWaitHang(): void {
    if (!this.dispatch) return;
    this.state.repPhase = 'wait_hang';
    this.dispatch(handleActivityStatus('wait hang'));
    this.dispatch(handleTimerStatus('stop'));
    console.log('SimpleWorkoutManager: Waiting for user to pull...');
  }

  /**
   * Transition to waiting-for-release state (after hang timer expires).
   * Called from ExerciseManager when the hang timer fires 'done'.
   * The rep is officially completed only once the user releases the hold.
   */
  public startWaitRelease(): void {
    if (!this.dispatch) return;
    this.state.repPhase = 'wait_release';
    this.dispatch(handleActivityStatus('wait release'));
    this.dispatch(handleTimerStatus('stop'));
    console.log('SimpleWorkoutManager: Hang timer done — waiting for user to release...');
  }

  /**
   * Complete the current rep and move to next
   */
  public completeRep(): void {
    if (!this.isInitialized() || !this.dispatch) {
      console.error('SimpleWorkoutManager: Not initialized');
      return;
    }

    console.log('SimpleWorkoutManager: Rep completed', {
      repIndex: this.state.currentRepIndex,
      totalReps: this.state.totalReps,
    });

    // Check if this was the last rep
    if (this.state.currentRepIndex >= this.state.totalReps - 1) {
      this.completeWorkout();
      return;
    }

    // Move to next rep
    this.state.currentRepIndex++;
    const nextRep = this.getCurrentRep();
    
    if (!nextRep) {
      console.error('SimpleWorkoutManager: No next rep found');
      return;
    }

    this.state.phase = 'active';
    this.dispatch(handleCurrentRepUpdate(nextRep));

    if (nextRep.intensity > 0) {
      // Next rep is a hang — wait for the user to pull before starting the timer
      this.state.repPhase = 'wait_hang';
      this.dispatch(handleActivityStatus('wait hang'));
      this.dispatch(handleTimerStatus('stop'));
    } else {
      // Next rep is a rest — start the rest timer immediately
      this.state.repPhase = 'rest';
      this.dispatch(handleActivityStatus('rest'));
      this.dispatch(handleTimerStatus('reset'));
      setTimeout(() => {
        this.dispatch(handleTimerStatus('start'));
      }, 50);
    }

    console.log('SimpleWorkoutManager: Moved to next rep', {
      repIndex: this.state.currentRepIndex,
      nextPhase: this.state.repPhase,
      time: nextRep.time,
      intensity: nextRep.intensity,
    });
  }

  /**
   * Complete the entire workout
   */
  public completeWorkout(): void {
    if (!this.dispatch) {
      console.error('SimpleWorkoutManager: Not initialized');
      return;
    }

    this.state.phase = 'completed';
    this.state.repPhase = 'wait_start';

    // Update Redux state
    this.dispatch(handleActivityStatus('reset'));
    this.dispatch(handleTimerStatus('stop'));

    console.log('SimpleWorkoutManager: Workout completed');
  }

  /**
   * Pause the workout
   */
  public pauseWorkout(): void {
    if (!this.dispatch) {
      console.error('SimpleWorkoutManager: Not initialized');
      return;
    }

    this.state.phase = 'paused';

    // Update Redux state
    this.dispatch(handleActivityStatus('pause'));
    this.dispatch(handleTimerStatus('pause'));

    console.log('SimpleWorkoutManager: Workout paused');
  }

  /**
   * Resume the workout
   */
  public resumeWorkout(): void {
    if (!this.config || !this.dispatch) {
      console.error('SimpleWorkoutManager: Not initialized');
      return;
    }

    this.state.phase = 'active';

    // Update Redux state
    this.dispatch(handleActivityStatus(this.state.repPhase));
    this.dispatch(handleTimerStatus('start'));

    console.log('SimpleWorkoutManager: Workout resumed');
  }

  /**
   * Reset the workout to initial state
   */
  public resetWorkout(): void {
    if (!this.dispatch) {
      console.error('SimpleWorkoutManager: Not initialized');
      return;
    }

    this.state = {
      ...this.getInitialState(),
      totalReps: this.config?.workout.content.reps.length || 0,
      isBluetoothReady: this.config?.bluetoothConnected && this.config?.bluetoothCalibrated || false,
    };

    // Update Redux state
    this.dispatch(handleActivityStatus('stop'));
    this.dispatch(handleTimerStatus('stop'));
    this.dispatch(handleCurrentRepUpdate(this.getCurrentRep()));

    console.log('SimpleWorkoutManager: Workout reset');
  }

  /**
   * Get the current rep
   */
  public getCurrentRep(): Rep | null {
    if (!this.isInitialized() || this.state.currentRepIndex >= this.state.totalReps) {
      return null;
    }
    return this.config!.workout.content.reps[this.state.currentRepIndex];
  }

  /**
   * Get the next rep (for display purposes)
   */
  public getNextRep(): Rep | null {
    if (!this.isInitialized() || this.state.currentRepIndex + 1 >= this.state.totalReps) {
      return null;
    }
    return this.config!.workout.content.reps[this.state.currentRepIndex + 1];
  }

  /**
   * Get current state
   */
  public getState(): WorkoutState {
    return { ...this.state };
  }

  /**
   * Check if workout is ready to start
   */
  public isReadyToStart(): boolean {
    return this.state.isBluetoothReady && this.state.phase === 'idle';
  }

  /**
   * Check if workout is active
   */
  public isActive(): boolean {
    return this.state.phase === 'active';
  }

  /**
   * Check if workout is paused
   */
  public isPaused(): boolean {
    return this.state.phase === 'paused';
  }

  /**
   * Check if workout is completed
   */
  public isCompleted(): boolean {
    return this.state.phase === 'completed';
  }

  /**
   * Get progress percentage (0-100)
   */
  public getProgress(): number {
    if (this.state.totalReps === 0) return 0;
    return Math.round((this.state.currentRepIndex / this.state.totalReps) * 100);
  }

  /**
   * Get rep type for display
   */
  public getRepType(): string {
    const currentRep = this.getCurrentRep();
    if (!currentRep) return 'Unknown';
    
    if (this.state.repPhase === 'wait_start') return 'GET READY';
    if (currentRep.intensity > 0) return 'HANG';
    return 'REST';
  }

  /**
   * Get expected intensity for current rep
   */
  public getExpectedIntensity(): number {
    const currentRep = this.getCurrentRep();
    return currentRep?.expectedStrength || 0;
  }

  /**
   * Get rep time for current rep
   */
  public getRepTime(): number {
    const currentRep = this.getCurrentRep();
    return currentRep?.time || 0;
  }

  /**
   * Check if the workout manager is properly initialized
   */
  public isInitialized(): boolean {
    return !!(this.config && this.config.workout && this.config.workout.content && this.config.workout.content.reps);
  }

  /**
   * Tear down the workout manager between sessions.
   * Call this in componentWillUnmount so the next ExerciseManager
   * mount can initialize with fresh workout data.
   */
  public teardown(): void {
    this.config = null;
    this.dispatch = null;
    this.state = this.getInitialState();
    console.log('SimpleWorkoutManager: Torn down (ready for next session)');
  }
}
